import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../services/supabaseClient';
import { 
  Building, Key, Users, TrendingUp, Activity, 
  ArrowUpRight, Clock, CheckCircle, Wallet, ChevronRight 
} from 'lucide-react';

export default function DashboardOverview() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    buildingsCount: 0,
    activeListings: 0,
    totalApplications: 0,
    potentialRevenue: 0,
    pipeline: { new: 0, docs: 0, visit: 0, approved: 0 }
  });

  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // 1. Immeubles
    const { count: bCount } = await supabase.from('habitat_buildings')
      .select('*', { count: 'exact', head: true })
      .eq('landlord_id', user.id);

    // 2. Annonces actives & Revenus potentiels
    const { data: listings } = await supabase.from('habitat_listings')
      .select('monthly_rent, status')
      .eq('landlord_id', user.id);
      
    const activeListings = listings?.filter(l => l.status === 'published') || [];
    const potentialRevenue = activeListings.reduce((sum, l) => sum + (l.monthly_rent || 0), 0);

    // 3. Candidatures (Pour le pipeline et l'activité récente)
    const { data: apps } = await supabase.from('habitat_applications')
      .select('id, status, tenant_name, created_at, listing:habitat_listings(title)')
      .eq('landlord_id', user.id)
      .order('created_at', { ascending: false });

    const pipeline = { new: 0, docs: 0, visit: 0, approved: 0 };
    if (apps) {
      apps.forEach(app => {
        if (pipeline[app.status as keyof typeof pipeline] !== undefined) {
          pipeline[app.status as keyof typeof pipeline]++;
        }
      });
    }

    setStats({
      buildingsCount: bCount || 0,
      activeListings: activeListings.length,
      totalApplications: apps?.length || 0,
      potentialRevenue,
      pipeline
    });

    setRecentActivity(apps?.slice(0, 4) || []); // Prendre les 4 dernières candidatures
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-teal-500"></div>
        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Analyse des données...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-500">
      
      {/* --- CARTES DE STATISTIQUES (KPIs) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Carte 1 : Immeubles */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:bg-slate-900 transition-colors">
              <Building className="text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Portefeuille</span>
          </div>
          <h3 className="text-4xl font-black text-slate-900 mb-1">{stats.buildingsCount}</h3>
          <p className="text-sm font-bold text-slate-500">Immeubles gérés</p>
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all text-teal-500">
            <ChevronRight size={20} />
          </div>
        </div>

        {/* Carte 2 : Annonces Actives */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-teal-50 rounded-2xl flex items-center justify-center group-hover:bg-teal-500 transition-colors">
              <Key className="text-teal-600 group-hover:text-white transition-colors" />
            </div>
            <span className="text-xs font-black text-teal-500 bg-teal-50 px-3 py-1 rounded-full uppercase tracking-widest">En ligne</span>
          </div>
          <h3 className="text-4xl font-black text-slate-900 mb-1">{stats.activeListings}</h3>
          <p className="text-sm font-bold text-slate-500">Unités disponibles</p>
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all text-teal-500">
            <ChevronRight size={20} />
          </div>
        </div>

        {/* Carte 3 : Candidatures */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center group-hover:bg-blue-500 transition-colors">
              <Users className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <span className="flex items-center gap-1 text-xs font-black text-blue-500 uppercase tracking-widest">
              <Activity size={12} /> Pipeline
            </span>
          </div>
          <h3 className="text-4xl font-black text-slate-900 mb-1">{stats.totalApplications}</h3>
          <p className="text-sm font-bold text-slate-500">Candidatures reçues</p>
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all text-teal-500">
            <ChevronRight size={20} />
          </div>
        </div>

        {/* Carte 4 : Revenus Potentiels (WOW Factor) */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-[2rem] shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/20 rounded-full blur-[40px] group-hover:scale-150 transition-transform duration-700"></div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20">
              <Wallet className="text-teal-400" />
            </div>
            <span className="flex items-center gap-1 text-xs font-black text-teal-400 uppercase tracking-widest">
              <TrendingUp size={12} /> Projection
            </span>
          </div>
          <h3 className="text-4xl font-black text-white mb-1 relative z-10">{stats.potentialRevenue.toLocaleString('fr-CA')} $</h3>
          <p className="text-sm font-bold text-slate-400 relative z-10">Revenus mensuels potentiels</p>
        </div>
      </div>

      {/* --- SECTION SECONDAIRE : TUNNEL & ACTIVITÉ --- */}
      <div className="grid lg:grid-cols-3 gap-6">
        
        {/* Graphique de Conversion (Funnel) */}
        <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
          <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
            <Activity className="text-teal-500" /> Tunnel d'acquisition locative
          </h3>
          
          <div className="space-y-6">
            {/* Ligne 1 : Nouveaux */}
            <div className="relative">
              <div className="flex justify-between items-end mb-2">
                <span className="text-sm font-bold text-slate-600 uppercase tracking-widest">1. Nouveaux Leads</span>
                <span className="text-lg font-black text-slate-900">{stats.pipeline.new}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div className="bg-blue-400 h-3 rounded-full" style={{ width: `${stats.totalApplications ? (stats.pipeline.new / stats.totalApplications) * 100 : 0}%` }}></div>
              </div>
            </div>

            {/* Ligne 2 : En analyse */}
            <div className="relative">
              <div className="flex justify-between items-end mb-2">
                <span className="text-sm font-bold text-slate-600 uppercase tracking-widest">2. En analyse</span>
                <span className="text-lg font-black text-slate-900">{stats.pipeline.docs}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div className="bg-orange-400 h-3 rounded-full" style={{ width: `${stats.totalApplications ? (stats.pipeline.docs / stats.totalApplications) * 100 : 0}%` }}></div>
              </div>
            </div>

            {/* Ligne 3 : Visites */}
            <div className="relative">
              <div className="flex justify-between items-end mb-2">
                <span className="text-sm font-bold text-slate-600 uppercase tracking-widest">3. Visites planifiées</span>
                <span className="text-lg font-black text-slate-900">{stats.pipeline.visit}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div className="bg-purple-400 h-3 rounded-full" style={{ width: `${stats.totalApplications ? (stats.pipeline.visit / stats.totalApplications) * 100 : 0}%` }}></div>
              </div>
            </div>

            {/* Ligne 4 : Approuvés */}
            <div className="relative">
              <div className="flex justify-between items-end mb-2">
                <span className="text-sm font-bold text-slate-600 uppercase tracking-widest">4. Approuvés</span>
                <span className="text-lg font-black text-green-600">{stats.pipeline.approved}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3 shadow-[0_0_10px_rgba(34,197,94,0.3)]">
                <div className="bg-green-500 h-3 rounded-full relative" style={{ width: `${stats.totalApplications ? (stats.pipeline.approved / stats.totalApplications) * 100 : 0}%` }}>
                  {stats.pipeline.approved > 0 && (
                    <div className="absolute top-0 right-0 bottom-0 left-0 bg-white/20 animate-pulse rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activité Récente */}
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
          <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
            <Clock className="text-slate-400" /> Activité récente
          </h3>
          
          {recentActivity.length === 0 ? (
            <div className="text-center py-10 text-slate-400 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              Aucune activité récente.
            </div>
          ) : (
            <div className="space-y-5">
              {recentActivity.map((activity, index) => (
                <div key={activity.id} className="flex gap-4 items-start group">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200 group-hover:bg-teal-50 group-hover:border-teal-200 transition-colors">
                    {activity.status === 'new' ? <ArrowUpRight size={16} className="text-blue-500" /> : <CheckCircle size={16} className="text-teal-500" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 leading-tight">
                      <span className="text-teal-600">{activity.tenant_name}</span> a postulé.
                    </p>
                    <p className="text-xs text-slate-500 mt-1 font-medium line-clamp-1">{activity.listing?.title}</p>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2">
                      {new Date(activity.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute:'2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
